import { ChevronDownIcon, ChevronRightIcon, LoaderCircleIcon } from "lucide-react";
import { useState } from "react";

import { usePolicyChunks, type PolicyChunk } from "../../../state/policies";
import { Badge } from "../../ui/badge";

function ChunkItem({ chunk, index }: { chunk: PolicyChunk; index: number }) {
  const [showContent, setShowContent] = useState(false);

  return (
    <div className="group px-4 py-3 transition-colors hover:bg-accent/30">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="flex size-5 shrink-0 items-center justify-center rounded bg-muted font-mono text-[10px] text-muted-foreground">
              {index + 1}
            </span>
            <span className="truncate text-sm font-medium">
              {chunk.sectionPath || chunk.title || "Untitled section"}
            </span>
          </div>
          {chunk.abstract ? (
            <p className="mt-1 ps-7 text-xs italic leading-relaxed text-muted-foreground">
              {chunk.abstract}
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {chunk.startPage !== null ? (
            <span className="text-[11px] text-muted-foreground tabular-nums">
              p.{chunk.startPage}
              {chunk.endPage !== null && chunk.endPage !== chunk.startPage
                ? `–${chunk.endPage}`
                : ""}
            </span>
          ) : chunk.tokenCount !== null ? (
            <span className="text-[11px] text-muted-foreground tabular-nums">
              {chunk.tokenCount} tok
            </span>
          ) : null}
          <Badge
            variant="outline"
            className="rounded-full px-2 py-0 font-mono text-[10px] font-normal"
          >
            {chunk.sectionType}
          </Badge>
        </div>
      </div>

      <div className="mt-2 ps-7">
        <button
          type="button"
          onClick={() => setShowContent((value) => !value)}
          className="flex cursor-pointer items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
        >
          {showContent ? (
            <ChevronDownIcon className="size-3" />
          ) : (
            <ChevronRightIcon className="size-3" />
          )}
          {showContent ? "Hide" : "Show"} content
          <span className="ms-1 font-mono text-[10px] text-muted-foreground/60">
            {chunk.vectorCount} vector{chunk.vectorCount === 1 ? "" : "s"}
          </span>
        </button>
        {showContent ? (
          <div className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap rounded-md border border-border/60 bg-muted/40 p-3 font-mono text-[11px] leading-relaxed text-foreground/80">
            {chunk.content}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * The sections this document was split into — exactly what the vector store
 * holds, shown verbatim.
 *
 * Loaded only when opened: the payload carries the embedded text, and nobody
 * reading a status page needs it until they ask.
 */
export function ChunksViewer({
  organizationId,
  policyId,
  open,
  onToggle,
}: {
  organizationId: string | null;
  policyId: string;
  open: boolean;
  onToggle: (open: boolean) => void;
}) {
  const { chunks, truncated, isPending, unavailable } = usePolicyChunks(
    organizationId,
    policyId,
    open,
  );

  return (
    <details
      className="group border-t border-border/60"
      open={open}
      onToggle={(event) => onToggle((event.currentTarget as HTMLDetailsElement).open)}
    >
      <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-3 hover:bg-accent/50">
        <ChevronRightIcon className="size-4 text-muted-foreground transition-transform group-open:rotate-90" />
        <span className="text-sm font-medium">Generated chunks</span>
        {chunks.length > 0 ? (
          <span className="text-xs text-muted-foreground tabular-nums">
            {chunks.length}
            {truncated ? "+" : ""}
          </span>
        ) : null}
      </summary>

      <div className="border-t border-border/60">
        {isPending && chunks.length === 0 ? (
          <div className="flex items-center justify-center p-8">
            <LoaderCircleIcon className="size-5 animate-spin text-muted-foreground" />
          </div>
        ) : unavailable ? (
          <p className="p-8 text-center text-sm text-muted-foreground">
            The vector store is not reachable from this environment, so its chunks cannot be
            listed.
          </p>
        ) : chunks.length === 0 ? (
          <p className="p-8 text-center text-sm text-muted-foreground">
            No chunks are stored for this document yet.
          </p>
        ) : (
          <>
            <div className="max-h-[600px] divide-y divide-border/60 overflow-y-auto">
              {chunks.map((chunk, index) => (
                <ChunkItem key={chunk.id} chunk={chunk} index={index} />
              ))}
            </div>
            {/* Say when the list is capped rather than presenting a partial
                index as the whole of it. */}
            {truncated ? (
              <p className="border-t border-border/60 px-4 py-2 text-xs text-muted-foreground">
                Showing the first {chunks.length} sections of a longer document.
              </p>
            ) : null}
          </>
        )}
      </div>
    </details>
  );
}
